/**
 * `newsletter` and `lead_capture` — enquiry forms that reach the seller over
 * WhatsApp (module 22 D3).
 *
 * We hold no email list and run no lead store, so honesty rules the design: a
 * submit opens WhatsApp to the store's own number with the entered details
 * pre-filled, exactly the channel spec 25 already uses. With no store phone
 * there's nowhere for an enquiry to go, so the form says so instead of quietly
 * dropping it. The forms are inert in the composer preview (the wrapper is
 * `inert`), so nothing submits there. Class-free (see `index.ts`).
 */
import type { FormEvent } from "react";
import { whatsappHref, type TargetContext } from "./resolve-target.js";
import { SectionHeading } from "./section-heading.js";

const INPUT_STYLE = {
  width: "100%",
  minWidth: 0,
  padding: "0.625rem 0.875rem",
  borderRadius: "var(--r-sm)",
  border: "1px solid var(--line)",
  background: "var(--paper)",
  color: "var(--ink)",
  fontSize: "0.9375rem",
} as const;

const BUTTON_STYLE = {
  padding: "0.625rem 1.5rem",
  borderRadius: "var(--r-sm)",
  background: "var(--accent)",
  color: "var(--accent-fg)",
  fontWeight: 600,
  fontSize: "0.9375rem",
  border: "none",
  cursor: "pointer",
  whiteSpace: "nowrap",
} as const;

function submitToWhatsApp(phone: string | null, text: string): void {
  if (!phone || typeof window === "undefined") return;
  window.open(`${whatsappHref(phone)}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
}

function Subtext({ children }: { children: string }) {
  return (
    <p
      style={{
        marginTop: "-0.75rem",
        marginBottom: "1.25rem",
        textAlign: "center",
        fontSize: "0.9375rem",
        color: "var(--muted)",
        maxWidth: "48ch",
        marginInline: "auto",
      }}
    >
      {children}
    </p>
  );
}

function NoPhoneNote({ phone }: { phone: string | null }) {
  if (phone) return null;
  return (
    <p style={{ marginTop: "0.75rem", textAlign: "center", fontSize: "0.75rem", color: "var(--muted)" }}>
      Add a store phone number to start receiving these.
    </p>
  );
}

export function Newsletter({
  heading,
  subtext,
  buttonLabel,
  ctx,
}: {
  heading: string;
  subtext?: string | undefined;
  buttonLabel?: string | undefined;
  ctx: TargetContext;
}) {
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = new FormData(e.currentTarget).get("email")?.toString().trim();
    if (email) submitToWhatsApp(ctx.phone, `New subscriber: ${email}`);
    e.currentTarget.reset();
  };
  return (
    <section aria-label={heading} className="flint-enquiry">
      <SectionHeading>{heading}</SectionHeading>
      {subtext ? <Subtext>{subtext}</Subtext> : null}
      <form className="flint-enquiry-row" onSubmit={onSubmit}>
        <input name="email" type="email" required placeholder="you@company.com" style={INPUT_STYLE} />
        <button type="submit" style={BUTTON_STYLE}>
          {buttonLabel || "Notify me"}
        </button>
      </form>
      <NoPhoneNote phone={ctx.phone} />
    </section>
  );
}

export function LeadCapture({
  heading,
  subtext,
  buttonLabel,
  ctx,
}: {
  heading: string;
  subtext?: string | undefined;
  buttonLabel?: string | undefined;
  ctx: TargetContext;
}) {
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const name = data.get("name")?.toString().trim() ?? "";
    const business = data.get("business")?.toString().trim() ?? "";
    const message = data.get("message")?.toString().trim() ?? "";
    submitToWhatsApp(
      ctx.phone,
      ["Dealer enquiry", name && `Name: ${name}`, business && `Business: ${business}`, message]
        .filter(Boolean)
        .join("\n"),
    );
    e.currentTarget.reset();
  };
  return (
    <section aria-label={heading} className="flint-enquiry" style={{ maxWidth: "40rem", marginInline: "auto" }}>
      <SectionHeading>{heading}</SectionHeading>
      {subtext ? <Subtext>{subtext}</Subtext> : null}
      <form onSubmit={onSubmit} style={{ display: "grid", gap: "0.75rem" }}>
        <div className="flint-enquiry-row">
          <input name="name" required placeholder="Your name" style={INPUT_STYLE} />
          <input name="business" placeholder="Business name" style={INPUT_STYLE} />
        </div>
        <textarea name="message" rows={3} placeholder="What are you looking for?" style={INPUT_STYLE} />
        <div style={{ textAlign: "center" }}>
          <button type="submit" style={BUTTON_STYLE}>
            {buttonLabel || "Send enquiry"}
          </button>
        </div>
      </form>
      <NoPhoneNote phone={ctx.phone} />
    </section>
  );
}
